<script setup>
import { TimelineMax, Back, Power1 } from "gsap/dist/gsap";

const route = useRoute();
const slug = route?.params?.slug;

const emit = defineEmits(["closeNav"]);

const props = defineProps({
  navigationItems: Array,
  active: {
    type: Boolean,
    default: false,
  },
});

function linkClicked() {
  emit("closeNav");
}

const openParents = ref(new Set());

function hasChildren(item) {
  return Array.isArray(item.children) && item.children.length > 0;
}

function isOpen(item) {
  return openParents.value.has(item.slug);
}

function toggleParent(item) {
  const next = new Set(openParents.value);
  if (next.has(item.slug)) next.delete(item.slug);
  else next.add(item.slug);
  openParents.value = next;
}

// Watch the `active` prop
watch(
  () => props.active,
  (newVal) => {
    if (newVal) openNavigation();
    else openParents.value = new Set(); // optional: reset when closing menu
  }
);

function openNavigation() {
  const openTl = new TimelineMax();

  openTl
    .fromTo(
      "#nav",
      0.25,
      { autoAlpha: 0 },
      { autoAlpha: 1, ease: Power1.easeOut },
      "start"
    )
    .staggerFromTo(
      document.getElementsByClassName("navigation__item"),
      0.4,
      { x: -60, autoAlpha: 0 },
      { x: 0, autoAlpha: 1, delay: 0.3, ease: Back.easeOut.config(1) },
      0.15,
      "start"
    );
}
</script>

<template>
  <nav id="navigation" class="navigation hidden z" :class="{ 'is--active': active }">
    <ul id="nav" class="px-sm sm:px-md md:px-lg xl:px-xl 2xl:px-2xl">
      <li
        v-for="item in navigationItems"
        :key="item.slug"
        class="navigation__item block w-full"
        :class="{ 'has--children': item.children?.length }"
      >
        <div class="flex items-center justify-start">
          <NuxtLink
            :to="item.slug === 'home' ? '/' : '/' + item.slug"
            class="block w-auto p-sm text-2xl lg:text-5xl 2xl:text-6xl font-bold hover:bg-gray-darkest"
            @click="linkClicked"
          >

            <span class="inline-flex items-center gap-2">
              <span>{{ item.title }}</span>


            </span>
          </NuxtLink>


          <!-- Toggle for parents (does NOT navigate) -->
        <button
          v-if="hasChildren(item)"
          type="button"
          class="navigation-parent-toggle p-sm transition-transform duration-200"
          :class="{ 'is--open': isOpen(item) }"
          :aria-expanded="isOpen(item) ? 'true' : 'false'"
          :aria-controls="`subnav-${item.slug}`"
          @click.stop="toggleParent(item)"
        >
          <span class="sr-only">
            {{ isOpen(item) ? "Sluit subnavigatie" : "Open subnavigatie" }}: {{ item.title }}
          </span>

          <!-- Chevron icon -->
          <span class="nav-chevron transition-transform duration-200">
            <IconChevronDown />
          </span>        
        </button>

        </div>

        <!-- Sub items -->
        <ul
          v-if="hasChildren(item)"
          :id="`subnav-${item.slug}`"
          class="navigation__sub"
          v-show="isOpen(item)"
        >
          <li
            v-for="child in item.children"
            :key="child.slug"
            class="navigation__subitem"
          >
            <NuxtLink
              :to="'/' + child.slug"
              :target="child.target ? child.target : '_self'"
              rel="noopener"
              class="block p-sm text-3xl hover:bg-gray-darkest/70"
              :class="child.slug === slug ? 'is--active' : ''"
              @click="linkClicked"
            >
              {{ child.title }}
            </NuxtLink>
          </li>
        </ul>
      </li>

      <!-- socials unchanged -->
      <li>
        <div class="navigation__item flex mt-md">
          <a
            href="https://www.instagram.com/wearehellosunshine/"
            class="flex items-center justify-center p-md hover:bg-gray-darkest w-[80px] lg:w-[120px] h-[80px] lg:h-[120px] cursor-pointer"
            aria-label="Ga naar Instagram"
          >
            <span><IconInstagram customClass="lg:w-[32px] lg:h-[32px]"/></span>
          </a>
          <a
            href="https://www.linkedin.com/company/hello-sunshine"
            class="flex items-center justify-center p-md hover:bg-gray-darkest w-[80px] lg:w-[120px] h-[80px] lg:h-[120px] cursor-pointer"
            aria-label="Ga naar Linkedin"
          >
            <span><IconLinkedin customClass="lg:w-[32px] lg:h-[32px]"/></span>

          </a>
        </div>
      </li>
    </ul>
  </nav>
</template>


<style>
.navigation {
  &.is--active {
    @apply absolute top-[60px] left-0 right-0 w-full;
    height: calc(100vh - 60px);
    padding: 60px 0 120px;
    @apply !block z-3;

    #nav {
      @apply bg-black absolute inset-0;
    }
  }

  ul {
    @apply flex flex-col justify-center items-start text-left;
    li {
      &.has--children {
        > a {
          @apply pr-[50px];
        }
        .navigation-parent-toggle {
          @apply transition;
        }
      }

      a {
        &.is--active {
          @apply text-red;
        }
      }
    }
  }
}

.navigation {
  .navigation-parent-toggle {
    @apply text-white;
  }

  .navigation-parent-toggle .nav-chevron > svg {
    @apply transition duration-300;
    transform: rotate(0deg);
  }

  .navigation-parent-toggle.is--open .nav-chevron > svg {
    @apply transition duration-300;
    transform: rotate(180deg);
  }
}


</style>
